/* ============================================================================
   LA BARRA DE PASOS DE LA PREPARACIÓN

   El orden de la preparación ya existía —no se calcula el peso y balance sin
   saber qué se carga— pero vivía en la cabeza del piloto. Esta barra lo pone
   en la pantalla.

   ══════════════════════════════════════════════════════════════════════════
   DECISIONES

   · **No dice «listo» sobre un dato que no tiene.** Un paso con un requisito
     sin dato se muestra incompleto, igual que uno con un requisito mal. La
     diferencia entre «falta» y «sin dato» se ve al abrirlo.

   · **Nombra lo primero que bloquea, no cuenta cuántas cosas son.** «Faltan 3
     cosas» manda a buscar por la pantalla; «falta el peso vacío de la
     matrícula» se resuelve.

   · **No se puede cerrar.** Es el estado de la preparación, no un aviso.

   · 44 px de alto en cada paso: se toca con guante.
   ========================================================================== */

import React, { useState } from "react";

import { estadoDelCamino, queFalta, SIN_DATO } from "../../domain/procesos.js";
import "./barraDePasos.css";

/** El punto de estado de cada paso. Forma distinta, no solo color. */
function Marca({ paso }) {
  if (paso.listo) return <span className="pasos__marca pasos__marca--listo" aria-hidden="true">✓</span>;
  if (paso.bloquea) return <span className="pasos__marca pasos__marca--bloquea" aria-hidden="true">!</span>;
  return <span className="pasos__marca pasos__marca--parcial" aria-hidden="true">·</span>;
}

export default function BarraDePasos({ hechos = {}, pasoActivo = null, alIr = () => {} }) {
  const camino = estadoDelCamino(hechos);
  const [abierto, setAbierto] = useState(null);

  const detalle = abierto && camino.pasos.find((p) => p.id === abierto);

  return (
    <section className="pasos" aria-label="Preparación del vuelo">
      <header className="pasos__cabecera">
        <h2 className="pasos__titulo">Preparación del vuelo</h2>

        {/* ═══ LA ÚNICA AFIRMACIÓN FUERTE ═══

            Verde solo cuando TODOS los requisitos que bloquean están
            cumplidos. Un dato ausente deja esto en ámbar: la aplicación no
            puede decir que se puede despegar sobre algo que no sabe. */}
        <span className={`pasos__veredicto pasos__veredicto--${camino.sePuedeVolar ? "listo" : "falta"}`}>
          {camino.sePuedeVolar
            ? "Preparación completa"
            : queFalta(camino)}
        </span>
      </header>

      <ol className="pasos__lista">
        {camino.pasos.map((p, i) => (
          <li key={p.id} className="pasos__item">
            <button
              type="button"
              className={`pasos__paso pasos__paso--${p.avance}`
                + (p.bloquea ? " pasos__paso--bloquea" : "")
                + (pasoActivo === p.id ? " pasos__paso--activo" : "")
                + (abierto === p.id ? " pasos__paso--abierto" : "")}
              aria-current={pasoActivo === p.id ? "step" : undefined}
              aria-expanded={abierto === p.id}
              onClick={() => setAbierto((a) => (a === p.id ? null : p.id))}
            >
              <span className="pasos__numero">{i + 1}</span>
              <span className="pasos__nombre">{p.nombre}</span>
              <Marca paso={p} />
            </button>
          </li>
        ))}
      </ol>

      {/* El detalle se abre acá abajo y no en un globo: en pantalla táctil un
          globo tapa justamente el paso que se está mirando. */}
      {detalle && (
        <div className="pasos__detalle">
          <p className="pasos__detalle-que">{detalle.descripcion}</p>

          <ul className="pasos__requisitos">
            {detalle.requisitos.map((r) => (
              <li key={r.clave} className={`pasos__req pasos__req--${r.estado}`}>
                <span className="pasos__req-marca" aria-hidden="true">
                  {r.estado === "cumplido" ? "✓" : r.estado === SIN_DATO ? "?" : "✗"}
                </span>
                <span className="pasos__req-texto">
                  {r.etiqueta}
                  {/* «Sin dato» y «falta» se resuelven distinto: uno se
                      averigua, el otro se corrige. */}
                  {r.estado === SIN_DATO && <em className="pasos__req-nota"> — sin dato</em>}
                  {!r.bloquea && <em className="pasos__req-nota"> · no impide volar</em>}
                </span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className="pasos__ir"
            onClick={() => { alIr(detalle.pestana); setAbierto(null); }}
          >
            Ir a {detalle.nombre}
          </button>
        </div>
      )}
    </section>
  );
}
